import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useApplicantStartChat } from '~/features/whatsapp/hooks/useApplicantStartChat';
import { useChats } from '~/features/whatsapp/hooks/useChats';
import { useOwnDivisionPhoneNumberId } from '~/features/whatsapp/hooks/useOwnDivisionPhoneNumberId';
import { openPanel } from '~/features/whatsapp/stores/chatPanelStore';
import { type LeadWithApplicants, resolveApplicantChats } from '~/features/whatsapp/utils/resolveApplicantChats';
import {
    OPEN_LEAD_WHATSAPP_ACTIONS,
    resolveOpenLeadWhatsAppAction,
} from '~/features/whatsapp/utils/resolveOpenLeadWhatsAppAction';
import { queryKeys } from '~/utils/queryKeys';

type LeadForOpenChat = LeadWithApplicants & { id: string };
type LoadedChats = NonNullable<ReturnType<typeof useChats>['data']>;

export const useOpenLeadWhatsAppChat = (lead: LeadForOpenChat | null | undefined) => {
    const { data: chats } = useChats();
    const ownPhoneNumberId = useOwnDivisionPhoneNumberId();
    const queryClient = useQueryClient();
    const [isStarting, setIsStarting] = useState(false);
    const [pendingOpen, setPendingOpen] = useState(false);
    const { kn1, kn2 } = useApplicantStartChat(lead?.id, lead ?? undefined);

    const slotFor = (slot: 1 | 2) => (slot === 1 ? kn1 : kn2);

    const resolveAndAct = (loadedChats: LoadedChats) => {
        if (!lead) return;

        const leadChats = loadedChats.filter((chat) => chat.loanRequestIds.includes(lead.id));
        const resolution = resolveApplicantChats(lead, leadChats, ownPhoneNumberId);

        const action = resolveOpenLeadWhatsAppAction({
            canStartKn1: kn1.payload != null,
            canStartKn2: kn2.payload != null,
            loanRequestId: lead.id,
            resolution,
        });

        if (action.kind === OPEN_LEAD_WHATSAPP_ACTIONS.OPEN_PANEL) {
            openPanel(action.options);
            return;
        }

        if (action.kind === OPEN_LEAD_WHATSAPP_ACTIONS.START_THEN_OPEN_CHAT) {
            setIsStarting(true);
            slotFor(action.slot)
                .start()
                .then(({ chatId }) => {
                    openPanel({
                        autoOpenTemplatePicker: true,
                        chatId,
                        leadDetailsScopeLoanRequestId: lead.id,
                    });
                })
                // Command errors are already toasted by useStartChatMutation's onError.
                .catch(() => undefined)
                .finally(() => setIsStarting(false));
            return;
        }

        // START_THEN_LIST: start every missing startable slot, wait for getChats to catch up
        // (there is always an active observer here — this hook itself calls useChats), then
        // list with pendingChatIds so ChatsListView spinners instead of flashing the empty prompt.
        setIsStarting(true);
        Promise.allSettled(action.slots.map((slot) => slotFor(slot).start()))
            .then(async (results) => {
                const pendingChatIds = results
                    .filter(
                        (settled): settled is PromiseFulfilledResult<{ chatId: string }> =>
                            settled.status === 'fulfilled',
                    )
                    .map((settled) => settled.value.chatId);

                await queryClient.invalidateQueries({ queryKey: queryKeys.getChats() });

                openPanel({ leadDetailsScopeLoanRequestId: lead.id, pendingChatIds });
            })
            // Promise.allSettled never rejects; invalidateQueries doesn't reject either.
            .catch(() => undefined)
            .finally(() => setIsStarting(false));
    };

    // getChats can still be a cold cache on the first click (e.g. right after navigating in), and
    // kn1/kn2 can still be loading too (e.g. useApplicantStartChat's own-corporation lookup for
    // phoneNumberId, which stays a live fetch here even though `lead` itself is preloaded). Wait
    // for both instead of resolving with stale/unknown eligibility, which used to strand a
    // startable lead on the empty lead-scoped list with no reason and no way to retry in place.
    useEffect(() => {
        if (!pendingOpen || chats === undefined || kn1.isLoading || kn2.isLoading) return;
        setPendingOpen(false);
        resolveAndAct(chats);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingOpen, chats, kn1.isLoading, kn2.isLoading]);

    const open = () => {
        if (!lead) return;
        if (chats === undefined || kn1.isLoading || kn2.isLoading) {
            setPendingOpen(true);
            return;
        }
        resolveAndAct(chats);
    };

    return { isStarting: isStarting || pendingOpen, open };
};
